import { Inject, Injectable } from '@angular/core';

import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccountingCommonService } from '../../common-api/accounting-common-api/api/accountingCommon.service';
import { ExchangeRateSearchRequest } from '../../common-api/accounting-common-api/model/exchangeRateSearchRequest';
import {
  CacheService,
  MEMORY_CACHE_SERVICE,
} from '../../common/services/cache.service';
import { isNullOrEmpty } from '../utils/common-functions';

const CURRENCY_EXCHANGE_RATE_CACHE_KEY = 'currencyExchangeRates';

@Injectable({
  providedIn: 'root',
})
export class CurrencyExchangeService {
  constructor(
    @Inject(MEMORY_CACHE_SERVICE) private cacheService: CacheService,
    private accountingCommonService: AccountingCommonService
  ) {}

  get exchangeRatesCache(): ExchangeRateItem[] {
    const cacheData = this.cacheService.get<ExchangeRateItem[]>(
      CURRENCY_EXCHANGE_RATE_CACHE_KEY
    );
    return cacheData ?? [];
  }

  getExchangeRate(
    srcCurrency: string,
    targetCurrency: string,
    exchangeDate?: string
  ): Observable<number> {
    if (srcCurrency === targetCurrency) {
      return of(1);
    }

    const dateStr = isNullOrEmpty(exchangeDate)
      ? DateTime.now().startOf('day').toFormat('yyyy-MM-dd')
      : exchangeDate!;
    const targetRateCache = this.exchangeRatesCache.find(
      (x) =>
        x.srcCurrency === srcCurrency &&
        x.targetCurrency === targetCurrency &&
        x.exchangeDate === dateStr
    );

    if (targetRateCache) {
      return of(targetRateCache.exchangeRate);
    }

    const reqObj: ExchangeRateSearchRequest = {
      filters: {
        baseCurrencyCd: srcCurrency,
        exchangeCurrencyCd: targetCurrency,
        quotedDate: DateTime.fromFormat(dateStr, 'yyyy-MM-dd').toJSDate(),
      },
    };
    const observable = this.accountingCommonService
      .accountingCommonExchangeRatePost(reqObj)
      .pipe(
        map((resp) => {
          if ((resp.exchangeRate ?? []).length === 0) {
            throw new Error('No exchange rates data.');
          }
          const targetRate = (resp.exchangeRate ?? [])[0];
          if (!targetRate.middleRate) {
            throw new Error('No middle rate in exchange rate data.');
          }

          const exchangeCacheItem: ExchangeRateItem = {
            exchangeDate: dateStr,
            exchangeRate: targetRate.middleRate,
            srcCurrency,
            targetCurrency,
          };
          const ratesCache = this.exchangeRatesCache.concat([
            exchangeCacheItem,
          ]);
          this.cacheService.add(CURRENCY_EXCHANGE_RATE_CACHE_KEY, ratesCache);

          return targetRate.middleRate;
        })
      );
    return observable;
  }
}

export interface ExchangeRateItem {
  exchangeDate: string;
  exchangeRate: number;
  srcCurrency: string;
  targetCurrency: string;
}
